import React, {useMemo, useState} from 'react';
import {getV2UnverifiedToken} from '@stripe-internal/data-v2';
import {useIntl} from '@sail/intl';

import Loading from 'src/components/core/Loading';
import {useResumeFundingSession} from 'src/fund/api/useResumeFundingSession';
import {FundingSessionContext} from 'src/fund/state/useFundingSessionContext';
import {FundingSessionDataProvider} from 'src/fund/state/FundingSessionDataProvider';
import {UserFacingError} from 'src/utils/error';
import messages from 'src/utils/messages';
import type {RetrieveFundingSessionQueryToken} from 'src/fund/api/useRetrieveFundingSessionQuery';

const errorForStatus = (status: number, intl: ReturnType<typeof useIntl>) => {
  switch (status) {
    case 400:
    case 404:
      return new UserFacingError(intl.formatMessage(messages.error404));
    case 410:
      return new UserFacingError(intl.formatMessage(messages.error410));
    default:
      return new Error(`Unexpected status: ${status}`);
  }
};

export const FundingSessionContextProvider = ({
  children,
  sessionHash,
}: {
  children: (token: RetrieveFundingSessionQueryToken) => React.ReactNode;
  sessionHash: string;
}): React.ReactElement => {
  const intl = useIntl();
  const {data, error, loading, status} = useResumeFundingSession(sessionHash);
  // This route's authorization comes from the scoped UAT returned by ILS.
  const token = useMemo(
    () => getV2UnverifiedToken(undefined, undefined, data?.apiKey),
    [data?.apiKey],
  );
  const [sessionCache, setSessionCache] = useState(() => ({
    apiKey: data?.apiKey,
    id: Symbol('funding-session-cache'),
  }));

  // Rotate before children render so different UATs never share cached data.
  if (sessionCache.apiKey !== data?.apiKey) {
    setSessionCache({
      apiKey: data?.apiKey,
      id: Symbol('funding-session-cache'),
    });
  }

  if (loading) {
    return <Loading />;
  }

  if (error) {
    throw error;
  }

  if (status && status !== 200) {
    throw errorForStatus(status, intl);
  }

  if (!data?.apiKey || !data.fundingSessionId) {
    throw errorForStatus(404, intl);
  }

  return (
    <FundingSessionContext.Provider value={{sessionHash, ...data}}>
      <FundingSessionDataProvider sharedCacheId={sessionCache.id}>
        {children(token)}
      </FundingSessionDataProvider>
    </FundingSessionContext.Provider>
  );
};
